import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getRestaurantId } from "../utils/JwtUtil";
import { MealFormModal } from "../components/meal/MealFormModal";
import { MenuJobModal } from "../components/meal/MenuJobModal";
import {
  Frown,
  Search,
  Filter,
  Plus,
  Upload,
  Edit,
  Eye,
  EyeOff,
  X,
  Donut,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../components/common/DataTable";
import {
  fetchMealsByRestaurant,
  deleteMealsBatch,
} from "../service/mealService";
import { fetchCategories } from "../service/categoryService";
import { S3_BASE_URL } from "../utils/Constants";

export default function Meals() {
  const { t, i18n } = useTranslation();
  const restaurantId = getRestaurantId();
  const qc = useQueryClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showMenuJobModal, setShowMenuJobModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingMeal, setEditingMeal] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mealTypeFilter, setMealTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  // Pagination handlers
  const handlePageChange = React.useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handlePageSizeChange = React.useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute(
      "dir",
      i18n.language === "ar" ? "rtl" : "ltr"
    );
  }, [i18n.language]);

  const {
    data: pageData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["meals", restaurantId, currentPage, pageSize],
    queryFn: () => fetchMealsByRestaurant(restaurantId, currentPage, pageSize),
    enabled: !!restaurantId,
    keepPreviousData: true,
  });

  // Fetch categories for filter dropdown
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const deleteMeals = useMutation({
    mutationFn: deleteMealsBatch,
    onSuccess: () => {
      qc.invalidateQueries(["meals", restaurantId]);
      setSelectedIds([]);
    },
  });

  if (!restaurantId) return <div className="p-6">{t("noRestaurant")}</div>;
  if (isLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  if (isError)
    return (
      <div className="alert alert-error shadow-lg m-6">
        <span>{error.message}</span>
      </div>
    );

  const { content = [], totalPages = 0, totalElements = 0 } = pageData || {};

  // Filter meals based on search and filter criteria
  const filteredMeals = content.filter((meal) => {
    const matchesSearch =
      searchQuery === "" ||
      meal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (meal.description &&
        meal.description.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "visible" && meal.isListed) ||
      (statusFilter === "hidden" && !meal.isListed);

    const matchesMealType =
      mealTypeFilter === "all" ||
      (meal.mealTypes && meal.mealTypes.includes(mealTypeFilter));

    const matchesCategory =
      categoryFilter === "all" || meal.categoryId.toString() === categoryFilter;

    return matchesSearch && matchesStatus && matchesMealType && matchesCategory;
  });

  const stats = {
    total: content.length,
    visible: content.filter((m) => m.isListed).length,
    hidden: content.filter((m) => !m.isListed).length,
  };

  const mealTypes = ["breakfast", "lunch", "dinner", "snack"];
  const activeFiltersCount = [
    statusFilter,
    mealTypeFilter,
    categoryFilter,
  ].filter((f) => f !== "all").length;

  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setMealTypeFilter("all");
    setCategoryFilter("all");
  };

  const allSelected =
    filteredMeals.length > 0 && selectedIds.length === filteredMeals.length;

  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? [] : filteredMeals.map((m) => m.id));
  const toggleSelect = (id) =>
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );

  const columns = [
    { key: "index", header: "#", isIndex: true, render: (_r, i) => i + 1 },
    {
      key: "name",
      header: t("name"),
      render: (r) => (
        <div className="flex items-center space-x-3">
          {r.imageUrl && (
            <img
              src={S3_BASE_URL + "/" + r.imageUrl}
              alt={r.name}
              className="w-10 h-10 rounded-lg object-cover"
            />
          )}
          <div>
            <div className="font-medium text-gray-900">{r.name}</div>
            {r.description && (
              <div className="text-sm text-gray-500 truncate max-w-xs">
                {r.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "price",
      header: t("price"),
      render: (r) => (
        <span className="font-medium text-gray-900">{r.price} JD</span>
      ),
    },
    {
      key: "category",
      header: t("categories"),
      render: (r) => {
        const category = categories.find((c) => c.id === r.categoryId);
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {category ? category.name : "-"}
          </span>
        );
      },
    },
    {
      key: "mealTypes",
      header: t("mealTypes.title"),
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {(r.mealTypes || []).map((type) => (
            <span
              key={type}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
            >
              {t(`mealTypes.${type}`)}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: "status",
      header: t("status.title"),
      render: (r) => (
        <div className="flex items-center space-x-2">
          {r.isListed ? (
            <>
              <Eye size={16} className="text-green-500" />
              <span className="text-green-600 text-sm font-medium">
                {t("visible")}
              </span>
            </>
          ) : (
            <>
              <EyeOff size={16} className="text-gray-500" />
              <span className="text-gray-500 text-sm font-medium">
                {t("hidden")}
              </span>
            </>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      header: t("actions"),
      render: (r) => (
        <button
          className="flex items-center space-x-1 px-2 py-1 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-800 transition-colors duration-200"
          onClick={() => setEditingMeal(r)}
          title={t("edit")}
        >
          <Edit size={14} />
          <span className="text-xs font-medium">{t("edit")}</span>
        </button>
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("meals")}</h1>
          <p className="text-gray-600 mt-1">
            Manage your restaurant's meals and menu items
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            onClick={() => setShowMenuJobModal(true)}
          >
            <Upload size={18} />
            <span>{t("uploadMenuAI")}</span>
          </button>
          <button
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            onClick={() => setShowModal(true)}
          >
            <Plus size={18} />
            <span>{t("createMeal")}</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Meals</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-blue-100 p-2 rounded-full">
              <span className="text-blue-600 text-lg">🍽️</span>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Visible Meals</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.visible}
              </p>
            </div>
            <div className="bg-green-100 p-2 rounded-full">
              <Eye className="text-green-600" size={20} />
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Hidden Meals</p>
              <p className="text-2xl font-bold text-gray-500">{stats.hidden}</p>
            </div>
            <div className="bg-gray-100 p-2 rounded-full">
              <EyeOff className="text-gray-500" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow border space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t("searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Filter size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">{t("allStatuses")}</option>
            <option value="visible">{t("visible")}</option>
            <option value="hidden">{t("hidden")}</option>
          </select>

          {/* Meal Type Filter */}
          <select
            value={mealTypeFilter}
            onChange={(e) => setMealTypeFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Meal Types</option>
            {mealTypes.map((type) => (
              <option key={type} value={type}>
                {t(`mealTypes.${type}`)}
              </option>
            ))}
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id.toString()}>
                {category.name}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X size={14} />
              <span>Clear ({activeFiltersCount})</span>
            </button>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow border">
        <DataTable
          data={filteredMeals}
          columns={columns}
          allSelected={allSelected}
          onToggleAll={toggleSelectAll}
          onToggleRow={toggleSelect}
          selectedRowKeys={selectedIds}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          emptyState={
            <div className="flex-1 flex flex-col items-center justify-center py-16">
              <Frown size={80} className="text-gray-300" />
              <p className="mt-4 text-xl text-gray-500">
                {searchQuery || activeFiltersCount > 0
                  ? "No meals match your search criteria"
                  : t("noMeals")}
              </p>
              {(searchQuery || activeFiltersCount > 0) && (
                <button
                  onClick={clearAllFilters}
                  className="mt-2 text-blue-600 hover:text-blue-800"
                >
                  Clear filters to see all meals
                </button>
              )}
            </div>
          }
          pagination={{
            paginationInfo: {
              totalPages: totalPages,
              totalElements: totalElements,
              number: currentPage,
              size: pageSize,
            },
            currentPage: currentPage,
            pageSize: pageSize,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
            isLoading: isLoading,
            hasError: isError,
          }}
        />
      </div>

      {/* Modals */}
      {showModal && <MealFormModal onClose={() => setShowModal(false)} />}
      {editingMeal && (
        <MealFormModal
          mealToEdit={editingMeal}
          onClose={() => setEditingMeal(null)}
        />
      )}
      {showMenuJobModal && (
        <MenuJobModal onClose={() => setShowMenuJobModal(false)} />
      )}
    </div>
  );
}
